import { Badge, Button, EmptyState, Input, Textarea, useToast } from '@orbind/ui'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { MOCK_MAIL_ACCOUNT, MOCK_MAIL_MESSAGES, clientOfAddress, type MockMailMessage } from '../mocks/mail.ts'
import { ExplorerBody, useExplorer, useExplorerConfig } from '../shell/explorer.tsx'
import '../features/work-item/wi.css'
import './mail.css'

/**
 * Conta Google conectada.
 *
 * No protótipo isso e estado global do app (`_shell.html:1576` — `gmailOn`),
 * comecando DESCONECTADO, e sobrevive a troca de modulo. Aqui o modulo guarda
 * o mesmo bit fora do componente para nao voltar a pedir conexao a cada
 * navegacao; um reload recomeca do zero, como no protótipo.
 */
let contaConectada = false

/** Hora do dia para mensagens de hoje; data curta para as anteriores. */
function formatListTime(iso: string) {
  const date = new Date(iso)
  const today = new Date()
  const sameDay =
    date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
  return sameDay
    ? new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date)
    : new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(date)
}

/** Data completa exibida no cabeçalho da mensagem aberta. */
function formatFullTime(iso: string) {
  const date = new Date(iso)
  const day = new Intl.DateTimeFormat('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' }).format(date)
  const time = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(date)
  return `${day} às ${time}`
}

type Composer = {
  mode: 'new' | 'reply' | 'forward'
  to: string
  subject: string
  text: string
}

/**
 * E-mail — regiao `showMail` do protótipo, com os dois estados que o design
 * define: **não conectado** (`showMail.html:2-11` no painel principal e
 * `_shell.html:333-340` na versao compacta do explorer) e conectado.
 *
 * A lista de conversas mora no explorer do SHELL (`ExplorerBody`); busca e
 * botao "Escrever" vem de `useExplorer`.
 *
 * A empresa de cada mensagem e DERIVADO do dominio do remetente contra o
 * cadastro de empresas (`clientOfAddress`), nunca gravado na mensagem.
 *
 * Dados mocados em `../mocks/mail.ts`: nao ha envio real de mensagem.
 */
export function MailPage() {
  const { id: selectedId = null } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { search } = useExplorer()

  const [conectado, setConectado] = useState(contaConectada)
  const [messages, setMessages] = useState<MockMailMessage[]>(MOCK_MAIL_MESSAGES)
  const [composer, setComposer] = useState<Composer | null>(null)

  const selected = messages.find((message) => message.id === selectedId) ?? null

  // abrir a mensagem marca como lida e fecha qualquer composicao pendente
  useEffect(() => {
    setComposer(null)
    if (selectedId === null) return
    setMessages((current) => current.map((message) => (message.id === selectedId ? { ...message, unread: false } : message)))
  }, [selectedId])

  const conectar = () => {
    contaConectada = true
    setConectado(true)
    toast.push({ tone: 'success', message: 'Gmail conectado. E-mails embutidos e notificações de chegada ativas.' })
  }

  const escrever = () => {
    if (!conectado) {
      toast.push({ tone: 'info', message: 'Conecte sua conta Google para escrever e-mails.' })
      return
    }
    setComposer({ mode: 'new', to: '', subject: '', text: '' })
    if (selectedId !== null) navigate('/mail')
  }

  useExplorerConfig({
    // protótipo `_shell.html:2908`
    footer: conectado
      ? `${messages.length} ${messages.length === 1 ? 'conversa' : 'conversas'} · sincronizado com Gmail`
      : 'Integração opcional por membro',
    onCreate: escrever,
  })

  const consulta = search.trim().toLowerCase()
  const rows = messages.filter((message) => {
    if (consulta === '') return true
    return (
      message.subject.toLowerCase().includes(consulta) ||
      message.senderName.toLowerCase().includes(consulta) ||
      message.snippet.toLowerCase().includes(consulta)
    )
  })

  const naoLidas = messages.filter((message) => message.unread).length

  const enviar = () => {
    if (!composer || composer.text.trim() === '') return
    const mensagem =
      composer.mode === 'new'
        ? 'Mensagem pronta para envio pelo Gmail integrado (simulação).'
        : composer.mode === 'reply'
          ? 'Resposta pronta para envio pelo Gmail integrado (simulação).'
          : 'Encaminhamento pronto para envio pelo Gmail integrado (simulação).'
    toast.push({ tone: 'success', message: mensagem })
    setComposer(null)
  }

  /**
   * Converter em demanda: navega para a criacao de demanda levando o conteudo
   * do e-mail e o cliente correlacionado (protótipo `_shell.html:3620`). O
   * pre-preenchimento viaja no state da rota — e o que a tela de criacao vai
   * ler quando existir.
   */
  const converterEmDemanda = (message: MockMailMessage) => {
    const empresa = clientOfAddress(message.senderAddress)
    navigate('/demands/new', {
      state: {
        prefill: {
          origem: 'mail',
          title: message.subject,
          description: message.bodyParagraphs.join('\n\n'),
          requesterName: message.senderName,
          requesterEmail: message.senderAddress,
          clientName: empresa,
        },
      },
    })
    toast.push({
      tone: 'success',
      message: empresa
        ? `Nova demanda pré-preenchida com o conteúdo do e-mail e o cliente ${empresa}.`
        : 'Nova demanda pré-preenchida com o conteúdo do e-mail.',
    })
  }

  const empresaSelecionado = selected ? clientOfAddress(selected.senderAddress) : null

  return (
    <>
      <ExplorerBody>
        {!conectado ? (
          /* versao compacta do estado nao conectado — `_shell.html:333-340` */
          <div className="mail__offpane">
            <span className="mail__officon mail__officon--sm" aria-hidden>
              <i className="fab fa-google" />
            </span>
            <span className="mail__offtitle">Integre seu Gmail</span>
            <span className="mail__offtext">
              Cada membro conecta a própria conta. Os e-mails aparecem embutidos aqui, com notificações de chegada e
              correlação com empresas cadastrados.
            </span>
            <Button variant="primary" icon="fab fa-google" onClick={conectar}>
              Conectar minha conta
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="wi-empty" style={{ padding: '44px 24px' }}>
            <div className="wi-empty__icon">
              <i className="fas fa-envelope-open" aria-hidden />
            </div>
            <span className="wi-empty__title">Nada encontrado</span>
            <span className="wi-empty__text">
              Nenhuma mensagem com "{search.trim()}" no remetente, assunto ou resumo.
            </span>
          </div>
        ) : (
          rows.map((message) => {
            const empresa = clientOfAddress(message.senderAddress)
            return (
              <button
                key={message.id}
                type="button"
                className={`wi-row mail__row${message.id === selectedId ? ' wi-row--on' : ''}`}
                onClick={() => navigate(`/mail/${message.id}`)}
              >
                <span className="mail__avatar mail__avatar--sm" aria-hidden>
                  {message.initials}
                </span>
                <span className="wi-row__main">
                  <span className="wi-row__top">
                    <span className={`mail__from${message.unread ? ' mail__from--unread' : ''}`}>{message.senderName}</span>
                    <span className="mail__time">{formatListTime(message.receivedAt)}</span>
                  </span>
                  <span className={`mail__rowsubject${message.unread ? ' mail__rowsubject--unread' : ''}`}>
                    {message.subject}
                  </span>
                  <span className="mail__snippet">{message.snippet}</span>
                  {empresa ? (
                    <span className="mail__rowtags">
                      <Badge tone="neutral" icon="fas fa-building">
                        Empresa: {empresa}
                      </Badge>
                    </span>
                  ) : null}
                </span>
                {/* nao lida: ponto do protótipo + negrito acima + texto para leitor de tela */}
                {message.unread ? (
                  <span className="mail__dot">
                    <span className="ob-sr-only">Não lida</span>
                  </span>
                ) : null}
              </button>
            )
          })
        )}
      </ExplorerBody>

      <div className="mail__scroll">
        {!conectado ? (
          /* painel principal do estado nao conectado — `showMail.html:2-11` */
          <div className="mail__center">
            <div className="mail__offpane mail__offpane--lg">
              <span className="mail__officon" aria-hidden>
                <i className="fab fa-google" />
              </span>
              <h1 className="mail__offheading">Seu e-mail, embutido na Orbind</h1>
              <span className="mail__offtext">
                Conecte sua conta Google para ler e-mails aqui dentro, receber notificações de chegada e correlacionar
                remetentes com os empresas cadastrados na administração.
              </span>
              <Button variant="primary" icon="fab fa-google" onClick={conectar}>
                Conectar minha conta Google
              </Button>
            </div>
          </div>
        ) : (
          <div className="mail__page">
            <div className="mail__account">
              <i className="fab fa-google" aria-hidden style={{ fontSize: 12, color: '#EA4335' }} />
              <span className="mail__accounttext">Embutido via integração Gmail · {MOCK_MAIL_ACCOUNT}</span>
              <Button
                variant="ghost"
                onClick={() => toast.push({ tone: 'info', message: 'Abrindo a conversa no Gmail (simulação).' })}
              >
                Abrir no Gmail
              </Button>
            </div>

            {composer?.mode === 'new' ? (
              <div className="mail__card">
                <h1 className="mail__subject">Escrever</h1>
                <div className="mail__composerfields">
                  <Input
                    value={composer.to}
                    aria-label="Destinatário"
                    placeholder="Para — endereço de e-mail"
                    onChange={(event) => setComposer({ ...composer, to: event.target.value })}
                  />
                  <Input
                    value={composer.subject}
                    aria-label="Assunto"
                    placeholder="Assunto"
                    onChange={(event) => setComposer({ ...composer, subject: event.target.value })}
                  />
                  <Textarea
                    rows={9}
                    value={composer.text}
                    aria-label="Texto da mensagem"
                    placeholder="Escreva a mensagem…"
                    onChange={(event) => setComposer({ ...composer, text: event.target.value })}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <Button
                    variant="primary"
                    icon="fas fa-paper-plane"
                    disabled={composer.to.trim() === '' || composer.text.trim() === ''}
                    onClick={enviar}
                  >
                    Enviar
                  </Button>
                  <Button variant="ghost" onClick={() => setComposer(null)}>
                    Descartar
                  </Button>
                </div>
              </div>
            ) : selected === null ? (
              <div className="mail__center">
                <EmptyState
                  icon="fas fa-envelope-open"
                  title="Selecione uma mensagem"
                  description={
                    naoLidas > 0
                      ? `${naoLidas} ${naoLidas === 1 ? 'mensagem não lida' : 'mensagens não lidas'} na lista ao lado. Abra uma para ler aqui dentro, responder, encaminhar ou converter em demanda.`
                      : 'Escolha uma mensagem na lista para ler aqui dentro, responder, encaminhar ou converter em demanda.'
                  }
                  action={
                    <Button variant="primary" icon="fas fa-pen" onClick={escrever}>
                      Escrever
                    </Button>
                  }
                />
              </div>
            ) : (
              <div className="mail__card">
                <h1 className="mail__subject">{selected.subject}</h1>

                <div className="mail__sender">
                  <span className="mail__avatar" aria-hidden>
                    {selected.initials}
                  </span>
                  <span className="mail__senderinfo">
                    <span className="mail__sendername">
                      {selected.senderName} <span className="mail__senderaddr">&lt;{selected.senderAddress}&gt;</span>
                    </span>
                    <span className="mail__sendermeta">para mim · {formatFullTime(selected.receivedAt)}</span>
                  </span>
                  {empresaSelecionado ? (
                    <Badge tone="info" icon="fas fa-building">
                      {empresaSelecionado}
                    </Badge>
                  ) : null}
                </div>

                <div className="mail__body">
                  {selected.bodyParagraphs.map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>

                <div className="mail__actions">
                  <Button
                    variant="secondary"
                    icon="fas fa-reply"
                    onClick={() =>
                      setComposer({
                        mode: 'reply',
                        to: selected.senderAddress,
                        subject: `Re: ${selected.subject}`,
                        text: `\n\nEm ${formatFullTime(selected.receivedAt)}, ${selected.senderName} escreveu:\n> ${selected.snippet}`,
                      })
                    }
                  >
                    Responder
                  </Button>
                  <Button
                    variant="secondary"
                    icon="fas fa-share"
                    onClick={() => setComposer({ mode: 'forward', to: '', subject: `Enc: ${selected.subject}`, text: '' })}
                  >
                    Encaminhar
                  </Button>
                  <span style={{ flex: 1 }} />
                  {selected.context ? (
                    <Button
                      variant="secondary"
                      icon="fas fa-link"
                      title={`${selected.context.kind} ${selected.context.code}`}
                      onClick={() => navigate(selected.context!.route)}
                    >
                      Abrir {selected.context.code}
                    </Button>
                  ) : null}
                  <Button variant="primary" icon="fas fa-inbox" onClick={() => converterEmDemanda(selected)}>
                    Converter em demanda
                  </Button>
                </div>

                {/* aqui composer so pode ser 'reply' ou 'forward': o modo
                    'new' foi tratado no ramo anterior */}
                {composer ? (
                  <div className="mail__composer">
                    <div className="mail__composerhead">
                      <i className={composer.mode === 'reply' ? 'fas fa-reply' : 'fas fa-share'} aria-hidden />
                      {composer.mode === 'reply'
                        ? `Respondendo a ${selected.senderName}`
                        : `Encaminhando "${selected.subject}"`}
                    </div>
                    {composer.mode === 'forward' ? (
                      <Input
                        value={composer.to}
                        aria-label="Destinatário do encaminhamento"
                        placeholder="Para — endereço de e-mail"
                        onChange={(event) => setComposer({ ...composer, to: event.target.value })}
                      />
                    ) : null}
                    <Textarea
                      rows={6}
                      value={composer.text}
                      aria-label={composer.mode === 'reply' ? 'Texto da resposta' : 'Texto do encaminhamento'}
                      placeholder={composer.mode === 'reply' ? 'Escreva a resposta…' : 'Escreva uma observação…'}
                      onChange={(event) => setComposer({ ...composer, text: event.target.value })}
                    />
                    <div style={{ display: 'flex', gap: 10 }}>
                      <Button
                        variant="primary"
                        icon="fas fa-paper-plane"
                        disabled={composer.text.trim() === '' || (composer.mode === 'forward' && composer.to.trim() === '')}
                        onClick={enviar}
                      >
                        Enviar
                      </Button>
                      <Button variant="ghost" onClick={() => setComposer(null)}>
                        Descartar
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}
